/**
 * Navigation & Background & Mobile Menu - Moon Moon Agency
 * Handles navigation, background filters, and mobile menu interactions
 */

/*********************************************************************************************
 * 🎯 NAVIGATION MODULE
 */
const NavigationManager = {
	// Local configuration
	config: {
		selectors: {
			header: 'header',
			megaMenuLink: '[data-megamenulink]',
			menuLink: '[data-menulink]',
			filter: '[data-filter]',
			cartFilter: '[data-drawer-cart]',
			searchContainer: '[data-search-container]',
			headerBurger: '[data-header-burger]',
			menuMobile: '[data-menu-mobile]',
			menuSublinks: '[data-menu-sublinks]'
		},
		classes: {
			active: 'active',
			open: 'mm-open',
			drawerOpen: 'mm-drawer-open',
			stopScrolling: 'stop-scrolling',
			mobileMenuSlided: 'mm-mobile-menu-elem-slided'
		},
		timing: {
			filterHideDelay: 300
		}
	},

	_activeMegaLink: null,
	_hoverTimer: null,

	/**
	 * Initialize navigation systems
	 */
	init() {
		this.initMegaMenus();
		this.initBackgroundFilter();
		this.initAccessibilityHandlers();
		this.initMegaMenuAccessibility();
		this.initMobileSubmenuAccessibility();
		this.initAnnouncementBarObserver();
		this.initScrollBehavior();
	},

	/**
	 * Initialize accessibility keyboard handlers for header buttons
	 */
	initAccessibilityHandlers() {
		// Burger menu button
		const burgerBtn = window.getElement('[data-header-burger]');
		if (burgerBtn) {
			burgerBtn.addEventListener('click', () => {
				window.mmMobileMenu();
				// aria-expanded géré dans MobileMenuManager.toggle()
			});
		}

		// Mobile menu close button
		const mobileMenuCloseBtn = window.getElement('[data-mobile-menu-close]');
		if (mobileMenuCloseBtn) {
			mobileMenuCloseBtn.addEventListener('click', () => {
				MobileMenuManager.close();
			});
		}

		// Search toggle buttons (mobile + desktop)
		window.getElements('[data-search-toggle]').forEach((searchBtn) => {
			searchBtn.addEventListener('click', () => {
				if (window.SearchManager?._isOpen) {
					window.mmHideSearch();
				} else {
					window.mmCloseMobileMenu();
					window.mmShowSearch(searchBtn);
				}
			});
		});

		// Mobile fake search button
		window.getElements('[data-mobile-search-fake]').forEach((btn) => {
			btn.addEventListener('click', () => {
				window.mmCloseMobileMenu();
				requestAnimationFrame(() => window.mmShowSearch(btn));
			});
		});

		// Cart toggle button
		const cartBtn = window.getElement('[data-cart-toggle]');
		if (cartBtn) {
			cartBtn.addEventListener('click', () => {
				window.mmOpenCart(cartBtn);
				window.mmCloseMobileMenu(true);
				window.mmHideSearch(true);
			});
		}

		// Global Escape key handler (close all overlays)
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				const mobileMenu = window.getElement(this.config.selectors.menuMobile);
				if (mobileMenu?.classList.contains(this.config.classes.open)) {
					window.mmCloseMobileMenu();
					burgerBtn?.focus(); // Return focus to burger button
				}
			}
		});

		window.log('info', 'NavigationManager', 'Accessibility handlers initialized');
	},

	/**
	 * Initialize mega menu and submenu positioning
	 */
	initMegaMenus() {
		const header = window.getElement(this.config.selectors.header);
		if (!header) {
			window.log('warn', 'NavigationManager', 'Header not found');
			return;
		}

		const headerHeight = header.offsetHeight;

		// Le panneau du mega menu doit toujours couvrir la nav principale :
		// sa largeur depend du nombre/libelle des entrees du menu (reglable dans le BO).
		// On ajoute le gutter du header pour garder a droite du dernier lien
		// le meme espace qu'a gauche du premier.
		const primaryMenu = header.querySelector('.mm-header-menu');
		const navRight = primaryMenu ? primaryMenu.getBoundingClientRect().right : 0;
		if (navRight > 0) {
			const gutter = primaryMenu.getBoundingClientRect().left;
			document.documentElement.style.setProperty('--mega-menu-min-width', `${Math.ceil(navRight + gutter)}px`);
		} else {
			// Nav desktop masquee (mobile) : on laisse la largeur maquette par defaut
			document.documentElement.style.removeProperty('--mega-menu-min-width');
		}

		// Position mega menus (data-megamenulink)
		window.getElements(this.config.selectors.megaMenuLink).forEach((menuElem) => {
			const megaMenu = menuElem.nextElementSibling;
			if (megaMenu?.hasAttribute('data-megamenu')) {
				megaMenu.style.top = 'calc(1px - var(--announcement-offset, 0px))';
				megaMenu.style.paddingTop = 'calc(var(--navbar-height) + var(--announcement-offset, 0px))';
			}
		});

		// Position submenus (data-menulink → sibling [data-megamenu])
		window.getElements(this.config.selectors.menuLink).forEach((menuElem) => {
			const submenu = menuElem.nextElementSibling?.hasAttribute('data-megamenu')
				? menuElem.nextElementSibling
				: null;
			if (submenu) {
				submenu.style.top = `${headerHeight - 1}px`;
			}
		});
	},

	/**
	 * Initialize mega menu keyboard accessibility
	 */
	initMegaMenuAccessibility() {
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const regularMenuLinks = window.getElements(this.config.selectors.menuLink);

		// Mega menu links (with dropdown)
		megaMenuLinks.forEach((link) => {
			const megaMenu = link.nextElementSibling;
			if (!megaMenu?.hasAttribute('data-megamenu')) return;

			// Click to toggle mega menu
			link.addEventListener('click', (e) => {
				e.preventDefault();
				this.toggleMegaMenu(link, megaMenu);
			});

			// Keyboard: Enter navigates to collection URL (WCAG pattern for link+submenu)
			// ArrowDown opens the mega menu and moves focus inside
			link.addEventListener('keydown', (e) => {
				if (e.key === 'ArrowDown') {
					e.preventDefault();
					const isOpen = link.getAttribute('aria-expanded') === 'true';
					if (!isOpen) {
						this.openMegaMenu(link, megaMenu);
					}
					const firstLink = megaMenu.querySelector('a');
					firstLink?.focus();
				}
			});

			// Hover with intent delay (120ms if no panel open, instant when switching)
			link.addEventListener('mouseenter', () => {
				clearTimeout(this._hoverTimer);
				this._hoverTimer = setTimeout(() => {
					// Close any open secondary submenus
					regularMenuLinks.forEach((menuLink) => {
						if (!menuLink.hasAttribute('data-megamenulink')) {
							const sub = menuLink.nextElementSibling?.hasAttribute('data-megamenu')
								? menuLink.nextElementSibling
								: null;
							if (sub) {
								sub.style.display = 'none';
								menuLink.setAttribute('aria-expanded', 'false');
							}
						}
					});
					this.openMegaMenu(link, megaMenu);
				}, this._activeMegaLink ? 0 : 120);
			});

			link.addEventListener('mouseleave', () => {
				clearTimeout(this._hoverTimer);
				this._hoverTimer = null;
			});
		});

		// Regular menu links and submenus
		regularMenuLinks.forEach((link) => {
			// Only add listener if it's NOT a mega menu link
			if (!link.hasAttribute('data-megamenulink')) {
				// Submenu is now a sibling of the button inside .mm-header-link-wrapper
				const submenu = link.nextElementSibling?.hasAttribute('data-megamenu')
					? link.nextElementSibling
					: null;
				const wrapper = link.parentElement;

				if (submenu && wrapper) {
					const closeMegaMenus = () => {
						this._activeMegaLink = null;
						clearTimeout(this._hoverTimer);
						window.getElements(this.config.selectors.megaMenuLink).forEach((megaLink) => {
							megaLink.setAttribute('aria-expanded', 'false');
							const mm = megaLink.nextElementSibling;
							if (mm?.hasAttribute('data-megamenu')) {
								if (typeof gsap !== 'undefined') {
									gsap.killTweensOf(mm);
									gsap.set(mm, { visibility: 'hidden', opacity: 0, pointerEvents: 'none' });
								} else {
									mm.style.visibility = 'hidden';
									mm.style.opacity = '0';
									mm.style.pointerEvents = 'none';
								}
							}
						});
					};

					// Mouse: listen on wrapper so submenu stays open when hovering over it
					wrapper.addEventListener('mouseenter', () => {
						closeMegaMenus();
						submenu.style.display = 'flex';
						link.setAttribute('aria-expanded', 'true');
						this.showBackgroundFilter();
					});
					wrapper.addEventListener('mouseleave', () => {
						submenu.style.display = 'none';
						link.setAttribute('aria-expanded', 'false');
						this.hideBackgroundFilter();
					});

					// Keyboard: Disclosure pattern — Enter/Space opens, Escape closes
					const openSubmenu = () => {
						submenu.style.display = 'flex';
						link.setAttribute('aria-expanded', 'true');
						submenu.querySelector('a')?.focus();
					};
					const closeSubmenu = () => {
						submenu.style.display = 'none';
						link.setAttribute('aria-expanded', 'false');
					};

					link.addEventListener('keydown', (e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							link.getAttribute('aria-expanded') === 'true' ? closeSubmenu() : openSubmenu();
						}
					});
					submenu.addEventListener('keydown', (e) => {
						if (e.key === 'Escape') {
							closeSubmenu();
							link.focus();
						}
					});
					wrapper.addEventListener('focusout', (e) => {
						if (!wrapper.contains(e.relatedTarget)) {
							closeSubmenu();
						}
					});
				} else {
					// This link has NO dropdown - close all menus on hover
					link.addEventListener('mouseenter', () => {
						this.closeAllMegaMenus();
					});
				}
			}
		});

		// Keyboard: close mega menus when focus leaves mm-header-menu nav
		const menuNav = window.getElement('.mm-header-menu');
		if (menuNav) {
			menuNav.addEventListener('focusout', (e) => {
				if (!menuNav.contains(e.relatedTarget)) {
					this.closeAllMegaMenus();
				}
			});
		}

		// Mouse leave header to close all menus
		const header = window.getElement(this.config.selectors.header);
		if (header) {
			header.addEventListener('mouseleave', () => {
				this.closeAllMegaMenus();
			});
		}

		// Escape key closes all mega menus and returns focus to the active trigger
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				const activeLink = Array.from(megaMenuLinks).find(
					(link) => link.getAttribute('aria-expanded') === 'true'
				);
				if (activeLink) {
					this.closeAllMegaMenus();
					activeLink.focus();
				}
			}
		});

		// Initialize mega menu product links hover/focus
		this.initMegaMenuProductLinks();

		window.log('info', 'NavigationManager', 'Mega menu accessibility initialized');
	},

	/**
	 * Initialize mega menu product links (replace inline onmouseenter)
	 */
	initMegaMenuProductLinks() {
		const productLinks = window.getElements('[data-mega-menu-products-link]');

		productLinks.forEach((link) => {
			const hideSelector = link.dataset.hideSelector;
			const showSelector = link.dataset.showSelector;

			// Hover event
			link.addEventListener('mouseenter', () => {
				this.showMegaMenuProducts(hideSelector, showSelector);
			});

			// Focus event (keyboard navigation)
			link.addEventListener('focus', () => {
				this.showMegaMenuProducts(hideSelector, showSelector);
			});
		});
	},

	/**
	 * Toggle mega menu state
	 */
	toggleMegaMenu(link, megaMenu) {
		const isOpen = link.getAttribute('aria-expanded') === 'true';
		if (isOpen) {
			this.closeMegaMenu(link, megaMenu);
		} else {
			this.closeAllMegaMenus(); // Close others first
			this.openMegaMenu(link, megaMenu);
		}
	},

	/**
	 * Open mega menu with GSAP animation (directional switch when another panel is already open)
	 */
	openMegaMenu(link, megaMenu) {
		// La search resterait visible en transparence derrière le megamenu
		if (window.SearchManager?._isOpen) {
			window.mmHideSearch(true); // keepFilterActive : le megamenu garde le background filter
		}

		const fromLink = this._activeMegaLink;
		this._activeMegaLink = link;
		link.setAttribute('aria-expanded', 'true');

		if (typeof gsap === 'undefined') {
			megaMenu.style.visibility = 'visible';
			megaMenu.style.opacity = '1';
			megaMenu.style.pointerEvents = 'auto';
			this.showBackgroundFilter();
			return;
		}

		gsap.killTweensOf(megaMenu);
		const fadeEls = [...megaMenu.querySelectorAll('[data-megamenu-fade]')];

		if (fromLink && fromLink !== link) {
			// Directional switch: content slides in direction of navigation
			const fromMenu = fromLink.nextElementSibling;
			if (fromMenu?.hasAttribute('data-megamenu')) {
				const allLinks = [...window.getElements(this.config.selectors.megaMenuLink)];
				const dir = allLinks.indexOf(link) > allLinks.indexOf(fromLink) ? 1 : -1;
				const fromFade = [...fromMenu.querySelectorAll('[data-megamenu-fade]')];

				fromLink.setAttribute('aria-expanded', 'false');
				gsap.killTweensOf(fromMenu);
				gsap.killTweensOf(fromFade);
				// Outgoing: content slides in direction, wrapper hides after (opacity only — no visibility change on children)
				gsap.to(fromFade, { opacity: 0, x: dir * -20, duration: 0.2, ease: 'power2.in' });
				gsap.delayedCall(0.2, () => {
					gsap.set(fromMenu, { visibility: 'hidden', pointerEvents: 'none', zIndex: 0 });
					gsap.set(fromFade, { clearProps: 'all' });
				});

				// Incoming: wrapper instantly visible, content arrives from opposite side
				gsap.set(megaMenu, { visibility: 'visible', pointerEvents: 'auto', opacity: 1, zIndex: 1 });
				gsap.set(fadeEls, { opacity: 0, x: dir * 20 });
				gsap.to(fadeEls, { opacity: 1, x: 0, duration: 0.8, delay: 0.08, stagger: 0.12, ease: 'power3.out', onComplete: () => {
					gsap.set(megaMenu, { zIndex: 0 });
					gsap.set(fadeEls, { clearProps: 'all' });
				}});
			}
		} else {
			// Fresh open: wrapper instantly visible, content rises from slight y offset
			gsap.set(megaMenu, { visibility: 'visible', pointerEvents: 'auto', opacity: 1 });
			gsap.set(fadeEls, { opacity: 0, y: 10 });
			gsap.to(fadeEls, { opacity: 1, y: 0, duration: 0.8, delay: 0.08, stagger: 0.12, ease: 'power3.out', onComplete: () => {
				gsap.set(fadeEls, { clearProps: 'all' });
			}});
		}

		window.LenisManager?.instance?.stop();
		this.showBackgroundFilter();
	},

	/**
	 * Close mega menu with GSAP fade-out
	 */
	closeMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'false');
		if (this._activeMegaLink === link) this._activeMegaLink = null;

		window.LenisManager?.instance?.start();

		if (typeof gsap !== 'undefined') {
			const fadeEls = [...megaMenu.querySelectorAll('[data-megamenu-fade]')];
			gsap.killTweensOf(megaMenu);
			gsap.killTweensOf(fadeEls);
			// Content fades out, wrapper hides after last element completes (opacity only — no visibility change on children)
			const tl = gsap.timeline({ onComplete: () => {
				gsap.set(megaMenu, { visibility: 'hidden', pointerEvents: 'none' });
				gsap.set(fadeEls, { clearProps: 'all' });
			}});
			tl.to(fadeEls, { opacity: 0, y: -4, duration: 0.15, stagger: 0.03, ease: 'power2.in' });
		} else {
			megaMenu.style.visibility = 'hidden';
			megaMenu.style.opacity = '0';
			megaMenu.style.pointerEvents = 'none';
		}
	},

	/**
	 * Track announcement bar visibility to adjust mega menu height via --announcement-offset
	 */
	initAnnouncementBarObserver() {
		const announcementBar = window.getElement('.mm-section-announcement-bar');
		if (!announcementBar) return;

		const observer = new IntersectionObserver(([entry]) => {
			const visibleHeight = entry.isIntersecting ? Math.round(entry.intersectionRect.height) : 0;
			document.documentElement.style.setProperty('--announcement-offset', `${visibleHeight}px`);
		}, { threshold: [0, 1] });

		observer.observe(announcementBar);
		window.log('info', 'NavigationManager', 'Announcement bar observer initialized');
	},

	/**
	 * Close all mega menus with GSAP fade-out
	 */
	closeAllMegaMenus() {
		this._activeMegaLink = null;
		clearTimeout(this._hoverTimer);
		this._hoverTimer = null;

		window.LenisManager?.instance?.start();

		window.getElements(this.config.selectors.megaMenuLink).forEach((link) => {
			link.setAttribute('aria-expanded', 'false');
			const megaMenu = link.nextElementSibling;
			if (megaMenu?.hasAttribute('data-megamenu')) {
				if (typeof gsap !== 'undefined') {
					const fadeEls = [...megaMenu.querySelectorAll('[data-megamenu-fade]')];
					gsap.killTweensOf(megaMenu);
					gsap.killTweensOf(fadeEls);
					// Instant close when leaving header (no animation)
					gsap.set(megaMenu, { visibility: 'hidden', pointerEvents: 'none', zIndex: 0 });
					gsap.set(fadeEls, { clearProps: 'all' });
				} else {
					megaMenu.style.visibility = 'hidden';
					megaMenu.style.opacity = '0';
					megaMenu.style.pointerEvents = 'none';
				}
			}
		});
		this.hideBackgroundFilter();
	},

	/**
	 * Show specific mega menu products
	 */
	showMegaMenuProducts(classElemsToHide, classElemToShow) {
		try {
			window.getElements(classElemsToHide).forEach((elem) => (elem.style.display = 'none'));
			const elemToShow = window.getElement(classElemToShow);
			if (elemToShow) {
				elemToShow.style.display = 'grid';
			}
			window.emit('global:mega-menu-products-switched', { hideSelector: classElemsToHide, showSelector: classElemToShow });
		} catch (error) {
			window.log('error', 'NavigationManager', 'Failed to switch mega menu products', { error: error.message });
		}
	},

	/**
	 * Initialize background filter hover behaviors
	 */
	initBackgroundFilter() {
		const elements = {
			header: window.getElement(this.config.selectors.header),
			searchContainer: window.getElement(this.config.selectors.searchContainer),
			filter: window.getElement(this.config.selectors.filter),
			cart: window.getElement(this.config.selectors.cartFilter)
		};

		if (!elements.header || !elements.filter || !elements.cart) {
			window.log('warn', 'NavigationManager', 'Background filter elements not found');
			return;
		}

		const { header, searchContainer, filter, cart } = elements;
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const menuLinks = window.getElements(this.config.selectors.menuLink);

		const toggleFilter = (show) => {
			filter.classList.toggle(this.config.classes.active, show);
			window.emit(`global:background-filter-${show ? 'activated' : 'deactivated'}`, { 
				trigger: show ? 'toggle-on' : 'toggle-off',
				source: 'navigation' 
			});
		};

		// Event listeners
		megaMenuLinks.forEach((link) => 
			link.addEventListener('mouseenter', () => toggleFilter(true))
		);
		
		menuLinks.forEach((link) =>
			link.addEventListener('mouseenter', () => {
				const searchOpen = searchContainer?.classList.contains(this.config.classes.open);
				if (!searchOpen) toggleFilter(false);
			})
		);

		header.addEventListener('mouseleave', () => {
			const searchOpen = searchContainer?.classList.contains(this.config.classes.open);
			const cartOpen = cart.classList.contains(this.config.classes.drawerOpen);
			
			if (!searchOpen && !cartOpen) {
				toggleFilter(false);
			}
		});

	},

	/**
	 * Show background filter
	 */
	showBackgroundFilter(zIndex) {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) {
			window.log('warn', 'NavigationManager', 'Background filter element not found');
			return;
		}
		
		filter.classList.add(this.config.classes.active);
		if (zIndex) filter.style.zIndex = zIndex;
		window.emit('global:background-filter-activated', { 
			trigger: 'show', 
			zIndex, 
			hasZIndex: !!zIndex 
		});
	},

	/**
	 * Hide background filter
	 */
	hideBackgroundFilter() {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) return;
		
		setTimeout(() => filter.style.removeProperty('z-index'), this.config.timing.filterHideDelay);
		filter.classList.remove(this.config.classes.active);
		window.emit('global:background-filter-deactivated', { 
			trigger: 'hide',
			delay: this.config.timing.filterHideDelay 
		});
	},

	/**
	 * Initialize mobile submenu accessibility (replace inline onclick)
	 */
	initMobileSubmenuAccessibility() {
		// Submenu trigger buttons (show next)
		const submenuTriggers = window.getElements('[data-mobile-submenu-trigger]');
		submenuTriggers.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuTrigger;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowNext(targetSelector);
				btn.setAttribute('aria-expanded', 'true');
			});
		});

		// Submenu back buttons (show prev)
		const submenuBackBtns = window.getElements('[data-mobile-submenu-back]');
		submenuBackBtns.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuBack;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowPrev(targetSelector);
				// Find corresponding trigger and update aria-expanded
				const trigger = window.getElement(`[data-mobile-submenu-trigger="${targetSelector}"]`);
				if (trigger) trigger.setAttribute('aria-expanded', 'false');
			});
		});

		// Submenu close buttons (close entire mobile menu)
		const submenuCloseBtns = window.getElements('[data-mobile-submenu-close]');
		submenuCloseBtns.forEach((btn) => {
			btn.addEventListener('click', () => {
				const targetSelector = btn.dataset.mobileSubmenuClose;
				const trigger = window.getElement(`[data-mobile-submenu-trigger="${targetSelector}"]`);
				if (trigger) trigger.setAttribute('aria-expanded', 'false');
				MobileMenuManager.close();
			});
		});

		window.log('info', 'NavigationManager', 'Mobile submenu accessibility initialized');
	},

	/**
	 * Hide header on scroll down, reveal on scroll up
	 */
	initScrollBehavior() {
		const header = window.getElement(this.config.selectors.header);
		const headerInner = window.getElement('[data-mm-header]');
		if (!header || !headerInner) return;

		const announcementBar = window.getElement('.mm-section-announcement-bar');

		let lastScrollY = window.scrollY;
		let ticking = false;

		const update = () => {
			const currentScrollY = window.scrollY;
			const isTransparent = headerInner.hasAttribute('data-header-transparent');
			const mobileMenuOpen = window.getElement(this.config.selectors.menuMobile)?.classList.contains(this.config.classes.open);
			const megaMenuOpen = Array.from(window.getElements(this.config.selectors.megaMenuLink)).some(
				(link) => link.getAttribute('aria-expanded') === 'true'
			);

			const headerHeight = header.getBoundingClientRect().height || parseInt(getComputedStyle(document.documentElement).getPropertyValue('--navbar-height'), 10) || 0;
			const announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;
			const hideThreshold = headerHeight + announcementHeight;

			headerInner.classList.toggle('mm-header--scrolled', !isTransparent || currentScrollY > announcementHeight);

			if (!mobileMenuOpen && !megaMenuOpen) {
				if (currentScrollY <= hideThreshold) {
					header.classList.remove('mm-section-header--hidden');
				} else if (currentScrollY > lastScrollY) {
					header.classList.add('mm-section-header--hidden');
				} else {
					header.classList.remove('mm-section-header--hidden');
				}
			}

			lastScrollY = currentScrollY;
			ticking = false;
		};

		this._scrollUpdate = update;
		update();

		window.addEventListener('scroll', () => {
			if (!ticking) {
				requestAnimationFrame(update);
				ticking = true;
			}
		}, { passive: true });

		window.log('info', 'NavigationManager', 'Scroll behavior initialized');
	},

	updateHeaderForPage(namespace) {
		const headerInner = window.getElement('[data-mm-header]');
		if (!headerInner) return;

		const isProduct = namespace === 'product';
		const isTransparent = namespace === 'index' || isProduct;

		headerInner.classList.toggle('mm-header--dark', isProduct);
		headerInner.toggleAttribute('data-header-transparent', isTransparent);

		this._scrollUpdate?.();
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		this.initMegaMenus();
	}
};

/*********************************************************************************************
 * 📱 MOBILE MENU MODULE
 */
const MobileMenuManager = {
	// Reference to NavigationManager config
	config: NavigationManager.config,

	// Focus trap state
	focusTrap: {
		isActive: false,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null,
		triggerElement: null
	},

	/**
	 * Setup focus trap for mobile menu
	 */
	setupFocusTrap(mobileMenu) {
		// Store trigger element to restore focus on close
		this.focusTrap.triggerElement = document.activeElement;

		const focusableElements = mobileMenu.querySelectorAll(
			'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab key to trap focus
		this.focusTrap.handleKeyDown = (e) => {
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		this.focusTrap.firstFocusable?.focus();

		window.log('info', 'MobileMenuManager', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;

			// Restore focus to the element that triggered the menu
			if (this.focusTrap.triggerElement) {
				this.focusTrap.triggerElement.focus();
				this.focusTrap.triggerElement = null;
			}

			window.log('info', 'MobileMenuManager', 'Focus trap removed');
		}
	},

	/**
	 * Toggle mobile menu
	 */
	toggle() {
		if (!window.isTablet()) return;

		const header = window.getElement(this.config.selectors.header);
		const icon = window.getElement(this.config.selectors.headerBurger);
		const mobileMenu = window.getElement(this.config.selectors.menuMobile);
		const mobileMenuSecondary = window.getElement(this.config.selectors.menuSublinks);
		const html = document.documentElement;

		if (!header || !icon || !mobileMenu || !mobileMenuSecondary) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found');
			return;
		}

		const headerHeight = header.offsetHeight;
		const headerMenuTop = header.getBoundingClientRect().top;
		const topPosition = `${headerHeight + headerMenuTop}px`;
		const heightCalc = `calc(var(--doc-height) - ${topPosition})`;

		[header, icon, mobileMenu].forEach((el) => el.classList.toggle(this.config.classes.open));

		mobileMenuSecondary.style.height = heightCalc;

		const isOpen = mobileMenu.classList.contains(this.config.classes.open);

		// Update burger aria-expanded (centralisé ici pour couvrir tous les cas d'appel)
		icon.setAttribute('aria-expanded', String(isOpen));

		if (isOpen) {
			mobileMenu.inert = false;
			mobileMenu.setAttribute('role', 'dialog');
			mobileMenu.setAttribute('aria-modal', 'true');
			html.classList.add(this.config.classes.stopScrolling);
			NavigationManager.showBackgroundFilter('var(--index-sm)');
			this.setupFocusTrap(mobileMenu);
			document.querySelectorAll('main, footer').forEach(el => el.inert = true);

			if (typeof gsap !== 'undefined') {
				const fadeEls = [...mobileMenu.querySelectorAll('[data-mobilemenu-fade]')];
				gsap.set(fadeEls, { autoAlpha: 0, y: 10 });
				gsap.to(fadeEls, { autoAlpha: 1, y: 0, duration: 0.35, delay: 0.08, stagger: 0.08, ease: 'power3.out', onComplete: () => {
					gsap.set(fadeEls, { clearProps: 'all' });
				}});
			}
		} else {
			if (typeof gsap !== 'undefined') {
				const fadeEls = [...mobileMenu.querySelectorAll('[data-mobilemenu-fade]')];
				gsap.killTweensOf(fadeEls);
				gsap.set(fadeEls, { clearProps: 'all' });
			}
			html.classList.remove(this.config.classes.stopScrolling);
			NavigationManager.hideBackgroundFilter();
			this.removeFocusTrap();
			document.querySelectorAll('[inert]').forEach(el => el.inert = false);
			mobileMenu.inert = true;
			mobileMenu.removeAttribute('role');
			mobileMenu.removeAttribute('aria-modal');
		}

		window.emit('global:mobile-menu-toggled', { headerHeight, topPosition });
	},

	/**
	 * Close mobile menu
	 */
	close(keepFilterActive = false) {
		if (!window.isTablet()) return;

		const header = window.getElement(this.config.selectors.header);
		const icon = window.getElement(this.config.selectors.headerBurger);
		const mobileMenu = window.getElement(this.config.selectors.menuMobile);
		const html = document.documentElement;

		if (!header || !icon || !mobileMenu) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found for closing');
			return;
		}

		if (typeof gsap !== 'undefined') {
			const fadeEls = [...mobileMenu.querySelectorAll('[data-mobilemenu-fade]')];
			gsap.killTweensOf(fadeEls);
			gsap.set(fadeEls, { clearProps: 'all' });
		}

		[header, icon, mobileMenu].forEach((el) => el.classList.remove(this.config.classes.open));
		html.classList.remove(this.config.classes.stopScrolling);

		// Reset all open submenus
		window.getElements(`.${this.config.classes.mobileMenuSlided}`).forEach((el) => {
			el.classList.remove(this.config.classes.mobileMenuSlided);
		});
		window.getElements('[data-mobile-submenu-trigger]').forEach((trigger) => {
			trigger.setAttribute('aria-expanded', 'false');
		});

		// Update burger aria-expanded
		icon.setAttribute('aria-expanded', 'false');

		// Restore background content and re-inert the mobile menu
		document.querySelectorAll('[inert]').forEach(el => el.inert = false);
		mobileMenu.inert = true;
		mobileMenu.removeAttribute('role');
		mobileMenu.removeAttribute('aria-modal');

		// Remove focus trap when menu closes
		this.removeFocusTrap();

		if (!keepFilterActive) NavigationManager.hideBackgroundFilter();
		window.emit('global:mobile-menu-closed', { keepFilterActive });
	},

	/**
	 * Show next sub-menu
	 */
	showNext(elemToShowClass) {
		const element = window.getElement(elemToShowClass);
		if (!element) return;

		element.classList.add(this.config.classes.mobileMenuSlided);

		if (typeof gsap !== 'undefined') {
			const fadeEls = [...element.querySelectorAll('[data-mobilesubmenu-fade]')];
			gsap.set(fadeEls, { autoAlpha: 0, y: 8 });
			gsap.to(fadeEls, { autoAlpha: 1, y: 0, duration: 0.3, delay: 0.05, stagger: 0.08, ease: 'power3.out', onComplete: () => {
				gsap.set(fadeEls, { clearProps: 'all' });
			}});
		}

		window.emit('global:mobile-submenu-next', { selector: elemToShowClass });
	},

	/**
	 * Show previous sub-menu
	 */
	showPrev(elemToShowClass) {
		const element = window.getElement(elemToShowClass);
		if (!element) return;

		// CSS slide back starts when class is removed (--duration-fast: 700ms)
		element.classList.remove(this.config.classes.mobileMenuSlided);

		if (typeof gsap !== 'undefined') {
			// Fade out submenu content
			const subFadeEls = [...element.querySelectorAll('[data-mobilesubmenu-fade]')];
			gsap.killTweensOf(subFadeEls);
			gsap.to(subFadeEls, { opacity: 0, duration: 0.15, ease: 'power2.in', onComplete: () => {
				gsap.set(subFadeEls, { clearProps: 'all' });
			}});

			// Re-animate main menu elements while submenu is sliding back
			const mobileMenu = window.getElement(this.config.selectors.menuMobile);
			if (mobileMenu) {
				const mainFadeEls = [...mobileMenu.querySelectorAll('[data-mobilemenu-fade]')];
				gsap.killTweensOf(mainFadeEls);
				gsap.set(mainFadeEls, { opacity: 0 });
				gsap.to(mainFadeEls, { opacity: 1, duration: 0.3, delay: 0.25, stagger: 0.06, ease: 'power3.out', onComplete: () => {
					gsap.set(mainFadeEls, { clearProps: 'all' });
				}});
			}
		}

		window.emit('global:mobile-submenu-prev', { selector: elemToShowClass });
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.NavigationManager = NavigationManager;
window.MobileMenuManager = MobileMenuManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
// Navigation functions
window.getMegaMenuPosition = () => NavigationManager.initMegaMenus();
window.mmMegaMenuShowProducts = (hide, show) => NavigationManager.showMegaMenuProducts(hide, show);

// Background filter functions
window.showBgFilter = (zIndex) => NavigationManager.showBackgroundFilter(zIndex);
window.hideBgFilter = () => NavigationManager.hideBackgroundFilter();

// Mobile menu functions
window.mmMobileMenu = () => MobileMenuManager.toggle();
window.mmCloseMobileMenu = (keep) => MobileMenuManager.close(keep);
window.mmMobileMenuShowNext = (elem) => MobileMenuManager.showNext(elem);
window.mmMobileMenuShowPrev = (elem) => MobileMenuManager.showPrev(elem);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	NavigationManager.init();
	// Mobile menu is closed by default: inert to remove it from tab order
	const mobileMenuEl = window.getElement('[data-menu-mobile]');
	if (mobileMenuEl) mobileMenuEl.inert = true;

	// cart:closed and mobile-menu-closed do a full [inert] sweep — re-apply closed overlays
	const reapplyInerts = () => {
		const menu = window.getElement('[data-menu-mobile]');
		if (menu && !menu.classList.contains('mm-open')) menu.inert = true;

		const searchContainer = window.getElement('[data-search-container]');
		if (searchContainer && !searchContainer.classList.contains('mm-open')) searchContainer.inert = true;
	};
	document.addEventListener('cart:closed', reapplyInerts);
	document.addEventListener('global:mobile-menu-closed', reapplyInerts);
});

window.addEventListener('resize', window.debounce(() => {
	NavigationManager.onResize();
}, 150));